import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { Wallet } from '../../models/Wallets';
import { WalletsFiltersPanel } from './WalletsFiltersPanel';

interface FiltersSectionProps {
  columns: ColumnDef<Wallet>[];
  selectedColumns: string[];
  fixedColumns?: string[];
  onColumnsChange: (selected: string[]) => void;
  searchValue: string;
  onSearch: (value: string) => void;
  filterConditions: FilterConditionOutput[];
  onFilterChange: (conditions: FilterConditionOutput[]) => void;
}

export const FiltersSection = ({
  columns,
  selectedColumns,
  fixedColumns = [],
  onColumnsChange,
  searchValue,
  onSearch,
  filterConditions,
  onFilterChange,
}: FiltersSectionProps) => {
  const { t } = useTranslation('walletsModule');
  const { t: tUI } = useTranslation('ui');

  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue((prev) => (prev === searchValue ? prev : searchValue));
  }, [searchValue]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    onSearch(inputValue.trim());
  }, [inputValue, onSearch]);

  const activeFiltersCount = filterConditions.length;

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="wallets-search"
        inputDataTestId="wallets-search-input"
      />

      <FiltersDropdown
        title={tUI('advancedFilter.filters', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="wallets-filters-toggle-button"
      >
        <WalletsFiltersPanel initialConditions={filterConditions} onChange={onFilterChange} />
      </FiltersDropdown>

      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        fixedColumns={fixedColumns}
        onColumnsChange={onColumnsChange}
        storageKey="wallets-ag-grid-table"
      />
    </>
  );
};
